import { AxiosResponse } from 'axios';

import { GARANT_ISSUE_DOC_TYPE, SIGN_DOC_TYPE } from '../../types';

import {
  IssueDetails,
  IssueInfoResponse,
  IssuePaymentRequest,
  OpenIssueFinanceRequest,
  OpenIssueFinanceResponse,
  OpenIssueRequest,
  OpenIssueResponse,
} from './dto/Issues';
import BaseApi from './BaseApi';

const issueApi = {
  initIssue(
    token: string,
    dto: OpenIssueRequest
  ): Promise<AxiosResponse<OpenIssueResponse>> {
    return BaseApi.post('api/v1/issues', dto, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  postRedeem(
    token: string,
    payload: { id: string; signature: string; initiatorId: number }
  ): Promise<AxiosResponse<OpenIssueResponse>> {
    return BaseApi.post(
      'api/v1/assets/redeem',
      {
        tokenId: payload.id,
        signature: payload.signature,
        initiatorId: payload.initiatorId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  initFinanceIssue(
    token: string,
    dto: OpenIssueFinanceRequest
  ): Promise<AxiosResponse<OpenIssueFinanceResponse>> {
    return BaseApi.post('api/v1/issues/finance', dto, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  initPayment(
    token: string,
    dto: IssuePaymentRequest
  ): Promise<AxiosResponse<OpenIssueResponse>> {
    return BaseApi.post('api/v1/issues/decision/payment', dto, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  getIssueInfo(
    token: string,
    infoIds: number[],
    accountId: number
  ): Promise<AxiosResponse<IssueInfoResponse[]>> {
    return BaseApi.get(
      `api/v1/issues?issuingInfoIds=${infoIds.length > 0 ? infoIds.join(',') : infoIds[0]
      }&accountId=${accountId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  getIssueDoc(
    token: string,
    decisionId: number,
    type?: SIGN_DOC_TYPE,
    contentType?: string
  ): Promise<AxiosResponse<ArrayBuffer>> {
    return BaseApi.get(
      `api/v1/issues/decision/${decisionId}/document${type ? '?type=' + type : ''
      }`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: contentType || 'application/pdf',
        },
        responseType: 'arraybuffer',
      }
    );
  },

  getAdditionalIssueDoc(
    token: string,
    decisionId: number,
    type?: GARANT_ISSUE_DOC_TYPE,
    contentType?: string
  ): Promise<AxiosResponse<ArrayBuffer>> {
    return BaseApi.get(
      `api/v1/issues/decision/${decisionId}/additional-document${type ? '?type=' + type : ''
      }`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: contentType || 'application/pdf',
        },
        responseType: 'arraybuffer',
      }
    );
  },

  signDecision(
    token: string,
    payload: {
      issuingDecisionId: number;
      hash: string;
      signature: string;
      accountId: number;
      documentType: SIGN_DOC_TYPE;
      checksum: string;
    }
  ): Promise<AxiosResponse<OpenIssueResponse>> {
    const { issuingDecisionId, ...restPayload } = payload;
    return BaseApi.post(
      `/api/v1/issues/decision/${issuingDecisionId}/sign`,
      restPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  getIssue(
    token: string,
    payload: {
      accountId: number;
      id: number;
    }
  ): Promise<AxiosResponse<IssueDetails>> {
    return BaseApi.get(
      `/api/v1/issues/${payload.id}/details?accountId=${payload.accountId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },
  paymentUpload(
    token: string,
    payload: {
      id: number;
      file: Blob;
    }
  ): Promise<AxiosResponse<any>> {
    const formData = new FormData();
    formData.append('document', payload.file);

    return BaseApi.post(
      `/api/v1/issues/finance/payment/upload?decisionId=${payload.id}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },
  paymentConfirm(
    token: string,
    payload: {
      id: number;
      signedData: string;
      privateKey: string;
    }
  ): Promise<AxiosResponse<any>> {
    const { id, ...restPayload } = payload;
    return BaseApi.post(
      `/api/v1/issues/finance/payment/confirm?decisionId=${payload.id}`,
      restPayload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },
  signIssue(
    token: string,
    payload: {
      decisionId: number;
      signedData: string;
      privateKey: string;
    }
  ): Promise<AxiosResponse<any>> {
    const { ...restPayload } = payload;
    return BaseApi.post(`/api/v1/issues/finance/issue/sign`, restPayload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },
};

export default issueApi;
